import L from "leaflet";
import React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { AttributionControl, ImageOverlay, MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet/dist/leaflet.css";
import MarkerClusterGroup from "./MarkerClusterGroup";
import "./map.scss";
import MapboxLogo from "./mapbox-logo-white.png";
import { LocationIcon } from "../Icons";
import style from "../../scss/export.module.scss";

export type TMap = {
    markers: TMapMarker[];
    mapType: EMapType;
    accessToken?: string;
    imageOverlay?: TImageOverlay;
    useSinglePin?: boolean;
};

export type TImageOverlay = {
    imageUrl: string;
    topLeftLat: number;
    topLeftLng: number;
    bottomRightLat: number;
    bottomRightLng: number;
};

export type TMapMarker = {
    lat: number;
    lng: number;
    popupContent?: React.ReactNode;
    markerText?: string;
    ariaLabel?: string;
};

export enum EMapType {
    DEFAULT = "default",
    MAPBOX_SATELLITE = "mapbox_satellite",
    MAPBOX_SATELLITE_STREETS = "mapbox_satellite_streets",
}

const TileLayerPreset = ({ mapType, accessToken }: { mapType: EMapType; accessToken?: string }) => {
    if (mapType === EMapType.DEFAULT) {
        return (
            <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                maxZoom={20}
                maxNativeZoom={19}
            />
        );
    } else if (mapType === EMapType.MAPBOX_SATELLITE) {
        return (
            <TileLayer
                attribution='&copy; <a href="https://www.mapbox.com/about/maps/" target="_blank">Mapbox</a> &copy; <a href="http://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> &copy; <a href="https://www.maxar.com" target="_blank">Maxar</a> <strong><a href="https://www.mapbox.com/map-feedback/" target="_blank">Improve this map</a></strong>'
                url={`https://api.mapbox.com/styles/v1/mapbox/satellite-v9/tiles/256/{z}/{x}/{y}?access_token=${accessToken}`}
                maxZoom={20}
            />
        );
    } else if (mapType === EMapType.MAPBOX_SATELLITE_STREETS) {
        return (
            <TileLayer
                attribution='&copy; <a href="https://www.mapbox.com/about/maps/" target="_blank">Mapbox</a> &copy; <a href="http://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> &copy; <a href="https://www.maxar.com" target="_blank">Maxar</a> <strong><a href="https://www.mapbox.com/map-feedback/" target="_blank">Improve this map</a></strong>'
                url={`https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v11/tiles/256/{z}/{x}/{y}?access_token=${accessToken}`}
                maxZoom={20}
            />
        );
    }

    return null;
};

export const Map = ({ markers, mapType = EMapType.DEFAULT, accessToken, imageOverlay, useSinglePin = false }: TMap) => {
    // Verify marker coordinates are valid numbers within reasonable bounds
    const verifyMarkerCoordinate = (marker: TMapMarker) => {
        const isValidLat = !isNaN(marker.lat) && marker.lat >= -90 && marker.lat <= 90;
        const isValidLng = !isNaN(marker.lng) && marker.lng >= -180 && marker.lng <= 180;
        return isValidLat && isValidLng;
    };
    const filteredMarkers = markers.filter(verifyMarkerCoordinate);

    return (
        <div className="c-map">
            <MapContainer
                className="c-map__container"
                scrollWheelZoom={true}
                zoomSnap={0.5}
                bounds={new L.LatLngBounds(filteredMarkers.map(marker => [marker.lat, marker.lng]))}
                boundsOptions={{ padding: [50, 50] }}
                minZoom={6}
                attributionControl={false} // Removes the default Leaflet attribution
            >
                <TileLayerPreset mapType={mapType} accessToken={accessToken} />
                <AttributionControl position="bottomright" prefix="" />

                {imageOverlay && (
                    <ImageOverlay
                        url={imageOverlay.imageUrl}
                        bounds={
                            new L.LatLngBounds(
                                [imageOverlay.topLeftLat, imageOverlay.topLeftLng],
                                [imageOverlay.bottomRightLat, imageOverlay.bottomRightLng]
                            )
                        }
                        opacity={1}
                        zIndex={10}
                    />
                )}
                {useSinglePin && filteredMarkers[0] && verifyMarkerCoordinate(filteredMarkers[0]) ? (
                    <Marker
                        position={[filteredMarkers[0].lat, filteredMarkers[0].lng]}
                        icon={L.divIcon({
                            className: "",
                            iconSize: [42, 42],
                            iconAnchor: [21, 42],
                            html: renderToStaticMarkup(
                                <div className="c-map__aria-wrapper" aria-label={filteredMarkers[0].ariaLabel}>
                                    <LocationIcon size={42} color={style.brightBlueColor} />
                                </div>
                            ),
                        })}
                    />
                ) : (
                    <MarkerClusterGroup
                        chunkedLoading
                        spiderfyDistanceMultiplier={2}
                        maxClusterRadius={zoom => {
                            if (zoom >= 19) return 7;
                            return 80;
                        }}
                    >
                        {filteredMarkers.map((marker, index) => (
                            <Marker
                                key={`${marker.lat}_${marker.lat}_${index}`}
                                position={[marker.lat, marker.lng]}
                                icon={L.divIcon({
                                    className: "",
                                    iconSize: [0, 0],
                                    html: renderToStaticMarkup(
                                        <div
                                            className="c-map__custom-marker"
                                            {...(!marker.markerText && { "aria-label": marker.ariaLabel })}
                                        >
                                            {marker.markerText}
                                        </div>
                                    ),
                                })}
                            >
                                <Popup>
                                    <div>{marker.popupContent}</div>
                                </Popup>
                            </Marker>
                        ))}
                    </MarkerClusterGroup>
                )}
            </MapContainer>
            {[EMapType.MAPBOX_SATELLITE, EMapType.MAPBOX_SATELLITE_STREETS].includes(mapType) && (
                <img className="c-map__mapbox-logo" src={MapboxLogo} width={50} alt="mapbox-logotype" />
            )}
        </div>
    );
};

export default Map;
